if (!customElements.get('shipping-protection')) {
  class ShippingProtection extends HTMLElement {
    static busy = false;
    static failedCorrection = null;

    connectedCallback() {
      this.input = this.querySelector('input');
      if (this.input) this.input.disabled = ShippingProtection.busy;
      this.onChange = this.change.bind(this);
      this.input?.addEventListener('change', this.onChange);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
        if (cartData && Array.isArray(cartData.items) && !ShippingProtection.busy) this.normalizeCart(cartData);
      });
      queueMicrotask(() => {
        if (this.isConnected && this.dataset.corrections) {
          this.correct(JSON.parse(`{${this.dataset.corrections}}`));
        }
      });
    }

    disconnectedCallback() {
      this.input?.removeEventListener('change', this.onChange);
      this.unsubscribe?.();
    }

    normalizeCart(cart) {
      const lines = cart.items.filter((item) => item.product_id === Number(this.dataset.productId));
      const merchandise = cart.items.some((item) => item.product_id !== Number(this.dataset.productId));
      const corrections = {};
      lines.forEach((item, index) => {
        const quantity = merchandise && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) corrections[item.key] = quantity;
      });
      if (Object.keys(corrections).length) this.correct(corrections);
    }

    correct(updates) {
      const fingerprint = JSON.stringify(updates);
      if (ShippingProtection.busy || ShippingProtection.failedCorrection === fingerprint) return;
      void this.update({ updates }, fingerprint);
    }

    change() {
      if (ShippingProtection.busy || document.querySelector('.cart__items--disabled')) {
        this.input.checked = this.dataset.selected === 'true';
        return;
      }
      ShippingProtection.failedCorrection = null;
      void this.update(this.input.checked
        ? { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] }
        : { removeProtection: true });
    }

    setBusy(busy) {
      document.querySelectorAll('shipping-protection').forEach((element) => {
        if (busy) element.setAttribute('aria-busy', 'true');
        else element.removeAttribute('aria-busy');
        const input = element.querySelector('input');
        if (input) input.disabled = busy;
      });
      document.querySelectorAll('#checkout, #CartDrawer-Checkout').forEach((button) => {
        if (busy) button.dataset.protectionWasDisabled = String(button.disabled);
        button.disabled = busy || button.dataset.protectionWasDisabled === 'true';
        if (!busy) delete button.dataset.protectionWasDisabled;
      });
    }

    async request(path, body) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const result = await response.json();
      if (!response.ok || result.errors || result.status >= 400) throw new Error(this.dataset.error);
      return result;
    }

    async update(body, correctionFingerprint) {
      if (ShippingProtection.busy) return;
      ShippingProtection.busy = true;
      this.setBusy(true);
      document.querySelectorAll('.shipping-protection__error').forEach((element) => { element.hidden = true; });
      const hadFocus = this.contains(document.activeElement);
      const inDrawer = Boolean(this.closest('cart-drawer'));
      try {
        const sections = this.getSections().map(({ section }) => section);
        if (body.removeProtection || body.items) {
          const cart = await this.request('cart.js');
          const lines = cart.items.filter((item) => item.product_id === Number(this.dataset.productId));
          if (body.removeProtection || lines.length) {
            const selected = !body.removeProtection;
            body = { updates: Object.fromEntries(lines.map((item, index) => [item.key, selected && index === 0 ? 1 : 0])) };
          } else if (!cart.items.length) {
            body = { updates: {} };
          }
        }
        const result = await this.request(body.items ? 'cart/add.js' : 'cart/update.js', {
          ...body, sections, sections_url: window.location.pathname,
        });
        const cart = body.items ? await this.request('cart.js') : result;
        let rendered = result.sections;
        if (!rendered || sections.some((section) => !rendered[section])) {
          const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
          if (!response.ok) throw new Error(this.dataset.error);
          rendered = await response.json();
        }
        this.render(rendered, cart);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
        if (hadFocus) document.querySelector(inDrawer
          ? '#CartDrawer shipping-protection input'
          : '#main-cart-footer shipping-protection input')?.focus();
      } catch (error) {
        if (correctionFingerprint) ShippingProtection.failedCorrection = correctionFingerprint;
        try {
          const cart = await this.request('cart.js');
          const sections = this.getSections().map(({ section }) => section);
          const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
          if (response.ok) this.render(await response.json(), cart);
        } catch {}
        document.querySelectorAll('shipping-protection').forEach((element) => {
          const input = element.querySelector('input');
          if (input) input.checked = element.dataset.selected === 'true';
          const message = element.querySelector('.shipping-protection__error');
          if (message) { message.textContent = element.dataset.error; message.hidden = false; }
        });
      } finally {
        ShippingProtection.busy = false;
        this.setBusy(false);
      }
    }

    getSections() {
      return [
        { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ...['main-cart-items', 'main-cart-footer'].filter((id) => document.getElementById(id)).map((id) => ({
          id, section: document.getElementById(id).dataset.id || id, selector: '.js-contents',
        })),
      ];
    }

    render(sections, cart) {
      const parser = new DOMParser();
      for (const { id, section, selector } of this.getSections()) {
        const target = id === 'cart-drawer' ? document.querySelector(selector)
          : document.getElementById(id)?.querySelector(selector) || document.getElementById(id);
        if (!target) continue;
        const fresh = sections[section] && parser.parseFromString(sections[section], 'text/html').querySelector(selector);
        if (!fresh) throw new Error(this.dataset.error);
        target.innerHTML = fresh.innerHTML;
      }
      document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-footer').forEach((element) => {
        element.classList.toggle('is-empty', cart.item_count === 0);
      });
      const drawer = document.querySelector('cart-drawer');
      const overlay = document.getElementById('CartDrawer-Overlay');
      if (overlay && drawer) overlay.onclick = () => drawer.close();
      if (drawer?.classList.contains('active')) {
        const focusTarget = drawer.querySelector('.drawer__close');
        trapFocus(drawer.querySelector('.drawer__inner'), focusTarget);
      }
    }
  }
  customElements.define('shipping-protection', ShippingProtection);
}
